<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useMeasurementUnit } from '~/composables/useMeasurementUnit';
import CustomSelect from '~/components/atoms/CustomSelect.vue';
import Button from '~/components/atoms/Button.vue';
import FramingExplainerAnim from '~/components/internal/FramingExplainerAnim.vue';
import { calculateFramingBreakdown, resolveFramingArtworkType, type FramingMode, type FramingSpecs } from '~/utils/framingCalculator';
import woodenPolishedTexture from '~/assets/textures/frame/wooden-polished.jpg';
import paintedWhiteTexture from '~/assets/textures/frame/painted-white.jpg';
import paintedBlackTexture from '~/assets/textures/frame/painted-black.jpg';

const props = defineProps<{
  artworkCategory: string; // 'Paper' | 'Canvas' | 'Sculpture' | 'Textile' | 'Mixed-Media'
  initialLength: number;
  initialHeight: number;
  sizeLabel: string;
  // A set prices these specs against every piece, so the calculator's own total is only one
  // piece of the bill there and says so. Single artworks keep the plain label.
  totalLabel?: string;
  // 'stretch' (canvas only) is the bars-only build: every other component is hidden and unpriced.
  mode?: FramingMode;
  // When true, shows one step at a time with Back/Next navigation instead of an accordion.
  stepByStep?: boolean;
  unit?: 'in' | 'cm';
}>();

const { globalUnit, formatDimensions } = useMeasurementUnit();
const activeUnit = computed(() => props.unit ?? globalUnit.value);

const unitLabelSuffix = computed(() => activeUnit.value === 'cm' ? 'cm' : 'inches');
const inUnitLabelSuffix = computed(() => activeUnit.value === 'cm' ? 'in cm' : 'in inches');

function formatDimensionValue(inches: number): string {
  if (activeUnit.value === 'cm') {
    const cm = Math.round(inches * 2.54 * 10) / 10;
    return `${Number.isInteger(cm) ? cm.toFixed(0) : cm.toFixed(1)} cm`;
  }
  return `${inches}`;
}

function formatDimensionWithUnit(inches: number): string {
  if (activeUnit.value === 'cm') {
    const cm = Math.round(inches * 2.54 * 10) / 10;
    return `${Number.isInteger(cm) ? cm.toFixed(0) : cm.toFixed(1)} cm`;
  }
  return `${inches} in`;
}

function formatStretcherBarOptionLabel(val: string): string {
  const parts = val.split('x').map(s => parseFloat(s.trim()));
  if (activeUnit.value === 'cm') {
    const converted = parts.map(p => {
      const cm = Math.round(p * 2.54 * 10) / 10;
      return Number.isInteger(cm) ? cm.toFixed(0) : cm.toFixed(1);
    });
    return `${converted.join(' x ')} cm`;
  }
  return parts.join(' x ');
}

function formatStretcherBarWithUnit(val: string): string {
  const parts = val.split('x').map(s => parseFloat(s.trim()));
  if (activeUnit.value === 'cm') {
    const converted = parts.map(p => {
      const cm = Math.round(p * 2.54 * 10) / 10;
      return Number.isInteger(cm) ? cm.toFixed(0) : cm.toFixed(1);
    });
    return `${converted.join(' x ')} cm`;
  }
  return `${parts.join(' x ')} in`;
}

const formattedSizeLabel = computed(() => {
  if (!props.sizeLabel) return '';
  return formatDimensions(props.sizeLabel, activeUnit.value);
});

const wantMount = ref(false);
// The Mount/Side Depth cost+options area clips its own content while sliding open (so the reveal
// animates instead of popping in) — but that same clipping would also cut off a <CustomSelect>'s
// dropdown inside it. Once the slide-open transition has actually finished, these flip to let
// that area overflow visibly again, so an open dropdown isn't clipped by its now-fully-expanded,
// otherwise-static parent. Resetting to false the instant the checkbox is unchecked matters: the
// closing animation needs clipping back on immediately, not just after the collapse finishes.
const mountExpandSettled = ref(false);
const sideDepthExpandSettled = ref(false);
watch(wantMount, (v) => { if (!v) mountExpandSettled.value = false; });
const frameColorOptions = [
  { key: 'polished', label: 'Wooden Polished', img: woodenPolishedTexture },
  { key: 'white', label: 'Wooden Painted White', img: paintedWhiteTexture },
  { key: 'black', label: 'Wooden Painted Black', img: paintedBlackTexture },
];
const frameColor = ref(frameColorOptions[0].key);

const withRecommended = (label: string, isRecommended: boolean) => isRecommended ? `${label} (recommended)` : label;

const mountTypeOptions = computed(() => ['Single Mount', 'Double Mount'].map(v => ({
  label: withRecommended(v, v === 'Single Mount'), value: v
})));
const mountWidthOptions = computed(() => [1, 1.5, 2, 2.5, 3].map(v => ({
  label: withRecommended(formatDimensionValue(v), v === 1), value: v
})));
// Only one glass/board option is actually offered right now, so it's shown plain (no
// "(recommended)" — redundant when it's the only choice) and the rest are disabled rather than
// removed, the same way a single-enabled-option CustomSelect already renders as non-interactive.
// Re-enabling more options later is just dropping their `disabled` line.
const glassTypeOptions = computed(() => [
  { label: 'Clear Modiguard - 2mm', value: 'Clear Modiguard-2mm' },
  { label: 'Clear Modiguard - 3.5mm', value: 'Clear Modiguard-3.5mm' },
  { label: 'Acrylic Sheet - 2mm', value: 'Acrylic Sheet-2mm' },
  { label: 'Acrylic Sheet - 3.5mm', value: 'Acrylic Sheet-3.5mm' }
].map(o => ({ ...o, disabled: o.value !== 'Clear Modiguard-2mm' })));
const boardTypeOptions = computed(() => ['White Sunboard', 'Western Backboard'].map(v => ({
  label: v, value: v, disabled: v !== 'Western Backboard'
})));
const recommendedStretcherBar = computed(() => Math.max(inLength.value, inHeight.value) > 29 ? '1x1' : '1.5x2');
const stretcherBarOptions = computed(() => [
  { raw: '1x1', value: '1x1' },
  { raw: '1x2', value: '1x2' },
  { raw: '1.5x2', value: '1.5x2' },
  { raw: '2x2', value: '2x2' }
].map(o => ({ ...o, label: withRecommended(formatStretcherBarOptionLabel(o.raw), o.value === recommendedStretcherBar.value) })));
const frameTypeOptions = computed(() => [
  { label: 'Classic Frame', value: 'Classic Frame', disabled: artworkType.value === 'Canvas' },
  { label: 'Floater Frame', value: 'Floater Frame', disabled: artworkType.value === 'Paper' }
]);
const recommendedFrameFront = computed(() => artworkType.value === 'Paper' ? 1 : 1.5);
const frameFrontOptions = computed(() => [0.5, 1, 1.5, 2, 2.5, 3].map(v => ({
  label: withRecommended(formatDimensionValue(v), v === recommendedFrameFront.value), value: v
})));
const frameDepthOptions = computed(() => [0.5, 1, 1.5, 2, 2.5, 3].map(v => ({
  label: withRecommended(formatDimensionValue(v), v === 2), value: v
})));
const floaterSpaceOptions = computed(() => [0.5, 1, 1.5, 2, 2.5, 3].map(v => ({
  label: withRecommended(formatDimensionValue(v), v === 1), value: v
})));
const frameMaterialOptions = computed(() => [
  { label: withRecommended('Wooden', true), value: 'Wooden' },
  { label: 'Aluminium', value: 'Aluminium', disabled: frameType.value === 'Floater Frame' }
]);
const frameFinishOptions = computed(() => [
  { label: 'Natural Lacquer', value: 'Natural Lacquer' },
  { label: 'Painted Polished', value: 'Painted Polished' },
  { label: 'Double shade', value: 'Double shade' }
].map(o => ({ ...o, label: withRecommended(o.label, artworkType.value === 'Paper' && o.value === 'Painted Polished') })));

const emit = defineEmits<{
  (e: 'update:total', price: number): void;
  (e: 'update:specifications', specs: Record<string, any>): void;
  (e: 'done'): void;
}>();

const artworkType = computed(() => resolveFramingArtworkType(props.artworkCategory));
const mode = computed<FramingMode>(() => props.mode === 'stretch' ? 'stretch' : 'frame');
const isStretchMode = computed(() => mode.value === 'stretch');

const inLength = ref(props.initialLength || 0);
const inHeight = ref(props.initialHeight || 0);

watch(() => props.initialLength, (val) => { if (val) inLength.value = val; }, { immediate: true });
watch(() => props.initialHeight, (val) => { if (val) inHeight.value = val; }, { immediate: true });

const mountWidth = ref(1);
const mountType = ref('Single Mount');

const glassType = ref('Clear Modiguard-2mm');
const boardType = ref('Western Backboard');
const sideDepthRate = ref(100);
const wantSideDepth = ref(false);
watch(wantSideDepth, (v) => { if (!v) sideDepthExpandSettled.value = false; });
const sheetRate = ref(20);

const stretcherBar = ref(Math.max(inLength.value, inHeight.value) > 29 ? '1x1' : '1.5x2');

const frameType = ref(artworkType.value === 'Paper' ? 'Classic Frame' : 'Floater Frame');
const frameMaterial = ref('Wooden');
const frameFront = ref(artworkType.value === 'Paper' ? 1 : 1.5);
const frameDepth = ref(2);
const floaterSpace = ref(1);
const frameFinish = ref('Painted Polished');

const openComps = ref<Record<string, boolean>>({
  mount: false,
  glass: false,
  board: false,
  sidedepth: false,
  stretcher: false,
  frame: false,
  sheet: false,
});
const toggleComp = (key: string) => {
  for (const k in openComps.value) {
    if (k !== key) openComps.value[k] = false;
  }
  openComps.value[key] = !openComps.value[key];
};

const INR = (n: number) => "₹" + Math.round(n).toLocaleString('en-IN');

// The spec selections, gathered in one object: this is both what the price engine consumes and
// what gets emitted, so a consumer can re-price these exact choices against another size.
const currentSpecs = computed<FramingSpecs>(() => ({
  wantMount: wantMount.value,
  mountType: mountType.value,
  mountWidth: mountWidth.value,
  glassType: glassType.value,
  boardType: boardType.value,
  wantSideDepth: wantSideDepth.value,
  sideDepthRate: sideDepthRate.value,
  sheetRate: sheetRate.value,
  stretcherBar: stretcherBar.value,
  frameType: frameType.value,
  frameMaterial: frameMaterial.value,
  frameFront: frameFront.value,
  frameDepth: frameDepth.value,
  floaterSpace: floaterSpace.value,
  frameFinish: frameFinish.value,
}));

const breakdown = computed(() => calculateFramingBreakdown({
  artworkType: artworkType.value,
  lengthIn: inLength.value,
  heightIn: inHeight.value,
  specs: currentSpecs.value,
  mode: mode.value,
}));

const mountResult = computed(() => breakdown.value.components.mount);
const glassResult = computed(() => breakdown.value.components.glass);
const boardResult = computed(() => breakdown.value.components.board);
const sideDepthResult = computed(() => breakdown.value.components.sideDepth);
const sheetResult = computed(() => breakdown.value.components.sheet);
const stretcherResult = computed(() => breakdown.value.components.stretcher);
const frameResult = computed(() => breakdown.value.components.frame);

watch(artworkType, (type) => {
  if (type === 'Canvas') {
    frameType.value = 'Floater Frame';
    frameFront.value = 1.5;
  } else {
    frameType.value = 'Classic Frame';
    frameFront.value = 1;
  }
}, { immediate: true });
watch(frameType, (type) => {
  if (type === 'Floater Frame') {
    frameMaterial.value = 'Wooden';
  }
});

// The whole specification list folds away, starting open everywhere. The total below it never
// folds — a collapsed panel still has to say what the build costs.
const isPanelOpen = ref(true);

const framingTotal = computed(() => breakdown.value.total);

// A size past the pricing sheet's largest rung leaves some components with no rate at all, so the
// total above is only the parts that happened to price — a partial number, and a partial number is
// a wrong number to charge or to print on an invoice. Nothing is charged online for these: the
// build is still the buyer's to choose and configure, and the team quotes it afterwards.
// This is where all four checkouts agree on that rule: the artwork page bills whatever is emitted
// here, and the set page and cart already price out-of-range pieces at zero themselves.
const isOutOfRange = computed(() => breakdown.value.outOfRange);

const chargeableTotal = computed(() => (isOutOfRange.value ? 0 : framingTotal.value));

watch(chargeableTotal, (val) => {
  emit('update:total', val);
}, { immediate: true });

// `currentSpecs` and `breakdown` are rebuilt on every selection change, so watching them covers
// every input the old per-ref list did.
watch([currentSpecs, breakdown, frameColor], () => {
  emit('update:specifications', {
    ...currentSpecs.value,
    artworkType: artworkType.value,
    mode: mode.value,
    floaterSpace: frameType.value === 'Floater Frame' ? floaterSpace.value : undefined,
    frameFinish: frameMaterial.value === 'Wooden' ? frameFinish.value : undefined,
    frameColor: (frameMaterial.value === 'Wooden' && (frameFinish.value === 'Painted Polished' || frameFinish.value === 'Double shade')) ? frameColor.value : null,
    calculatedTotal: chargeableTotal.value,
    // Stored on the order so the team can see at a glance which builds still need quoting.
    outOfRange: isOutOfRange.value,
    components: breakdown.value.components,
  });
}, { immediate: true });

// ── Step-by-step support ─────────────────────────────────────────────────────
// Only active when `stepByStep` prop is true; accordion mode is completely unchanged.

interface FramingStep {
  key: 'frame' | 'stretcher' | 'mountorsidedepth' | 'glazing' | 'summary';
  label: string;
}

const currentStepIndex = ref(0);

const steps = computed<FramingStep[]>(() => {
  if (isStretchMode.value) {
    return [{ key: 'stretcher', label: 'Stretcher Bars' }];
  }
  // "Frame type" is no longer shown as its own field in this step (it's picked automatically —
  // Classic for Paper, Floater for Canvas — see frameType's default above), so the step title
  // carries that distinction instead of leaving it invisible to the buyer.
  const s: FramingStep[] = [
    { key: 'frame', label: frameType.value === 'Floater Frame' ? 'Frame (Floater)' : 'Frame' },
  ];
  if (artworkType.value !== 'Canvas') {
    s.push({ key: 'mountorsidedepth', label: 'Mount / Side Depth' });
    // No separate Glazing step: glass / board / sheet use their defaults and are only listed
    // (read-only) on the Summary step. The 'glazing' step markup is kept but no longer reached.
  } else {
    s.push({ key: 'stretcher', label: 'Stretcher Bars' });
  }
  s.push({ key: 'summary', label: 'Summary' });
  return s;
});

// Read-only recap of every selection for the final step. Each group is one priced component;
// `cost` is a number, 'tbc' (out of range / no rate), or null (nothing to show for it).
interface SummaryGroup {
  title: string;
  rows: { label: string; value: string }[];
  cost: number | 'tbc' | null;
}
const summaryGroups = computed<SummaryGroup[]>(() => {
  const cost = (r: { outOfRange?: boolean; rateNotDefined?: boolean; cost?: number }): number | 'tbc' =>
    r.outOfRange || r.rateNotDefined ? 'tbc' : (r.cost ?? 0);
  const groups: SummaryGroup[] = [];

  if (!isStretchMode.value) {
    const rows = [
      { label: 'Type', value: frameType.value },
      { label: 'Material', value: frameMaterial.value },
      { label: 'Front width', value: formatDimensionWithUnit(frameFront.value) },
      { label: 'Depth', value: formatDimensionWithUnit(frameDepth.value) },
    ];
    if (frameType.value === 'Floater Frame') rows.push({ label: 'Floater space', value: formatDimensionWithUnit(floaterSpace.value) });
    if (frameMaterial.value === 'Wooden') rows.push({ label: 'Finish', value: frameFinish.value });
    if (frameMaterial.value === 'Wooden' && (frameFinish.value === 'Painted Polished' || frameFinish.value === 'Double shade')) {
      rows.push({ label: 'Color', value: frameColorOptions.find(c => c.key === frameColor.value)?.label ?? '' });
    }
    groups.push({ title: 'Frame', rows, cost: frameResult.value.applicable ? cost(frameResult.value) : null });
  }

  if (isStretchMode.value || artworkType.value === 'Canvas') {
    groups.push({
      title: 'Stretcher Bars',
      rows: [{ label: 'Size', value: formatStretcherBarWithUnit(stretcherBar.value) }],
      cost: stretcherResult.value.applicable ? cost(stretcherResult.value) : null,
    });
  } else {
    groups.push({
      title: 'Mount',
      rows: wantMount.value
        ? [{ label: 'Type', value: mountType.value }, { label: 'Width', value: formatDimensionWithUnit(mountWidth.value) }]
        : [{ label: 'Selected', value: 'No' }],
      cost: wantMount.value ? cost(mountResult.value) : null,
    });
    groups.push({
      title: 'Side Depth',
      rows: [{ label: 'Selected', value: wantSideDepth.value ? 'Yes' : 'No' }],
      cost: wantSideDepth.value ? cost(sideDepthResult.value) : null,
    });
    groups.push({
      title: 'Glass',
      rows: [{ label: 'Type', value: glassTypeOptions.value.find(o => o.value === glassType.value)?.label ?? glassType.value }],
      cost: glassResult.value.applicable ? cost(glassResult.value) : null,
    });
    // Board and sheet are shown as one line item with their costs summed.
    const parts = [boardResult.value, sheetResult.value].filter(r => r.applicable).map(cost);
    groups.push({
      title: 'Board & Sheet',
      rows: [{ label: 'Type', value: boardType.value }],
      cost: !parts.length ? null
        : parts.includes('tbc') ? 'tbc'
        : (parts as number[]).reduce((sum, p) => sum + p, 0),
    });
  }
  return groups;
});

const currentStep = computed(() => steps.value[currentStepIndex.value] ?? steps.value[0]);
// One-line explainer shown right under the step title in the shared header — only the steps that
// need one set it, the rest render no subtitle at all.
const currentStepSubtitle = computed(() => {
  return '';
});
const isFirstStep = computed(() => currentStepIndex.value === 0);
const isLastStep  = computed(() => currentStepIndex.value === steps.value.length - 1);

function isStepValid(stepIndex: number): boolean {
  const s = steps.value[stepIndex];
  if (!s) return true;
  if (s.key === 'mountorsidedepth') {
    if (artworkType.value !== 'Paper') return true;
    return wantMount.value || wantSideDepth.value;
  }
  return true;
}

const isCurrentStepValid = computed(() => isStepValid(currentStepIndex.value));

function canGoToStep(targetIndex: number): boolean {
  if (targetIndex === currentStepIndex.value) return true;
  if (targetIndex < currentStepIndex.value) return true;
  for (let i = currentStepIndex.value; i < targetIndex; i++) {
    if (!isStepValid(i)) return false;
  }
  return true;
}

function goToStep(targetIndex: number) {
  if (canGoToStep(targetIndex)) {
    currentStepIndex.value = targetIndex;
  }
}

function goNext() { if (!isLastStep.value && isCurrentStepValid.value) currentStepIndex.value++; }
function goPrev() { if (!isFirstStep.value) currentStepIndex.value--; }

// Reset to step 1 whenever the mode or artwork type changes so stale indices don't linger.
watch([isStretchMode, artworkType], () => { currentStepIndex.value = 0; });

// The cost relevant to the current step (null = out of range / not priced).
const currentStepCost = computed<number | null>(() => {
  const key = currentStep.value?.key;
  const r = (result: { outOfRange?: boolean; rateNotDefined?: boolean; cost?: number }) =>
    result.outOfRange || result.rateNotDefined ? null : (result.cost ?? 0);
  if (key === 'frame')     return r(frameResult.value);
  if (key === 'stretcher') return r(stretcherResult.value);
  if (key === 'mountorsidedepth') {
    const mount = wantMount.value ? r(mountResult.value) : 0;
    const sideDepth = wantSideDepth.value ? r(sideDepthResult.value) : 0;
    return mount === null || sideDepth === null ? null : mount + sideDepth;
  }
  if (key === 'glazing') {
    const parts = [r(glassResult.value), r(boardResult.value), r(sheetResult.value)];
    return parts.some(p => p === null) ? null : parts.reduce((sum, p) => sum! + p!, 0);
  }
  return null;
});
</script>
<template>

  <!-- ════════════════════════════════════════════════════════════════════════
       STEP-BY-STEP MODE  (stepByStep prop = true)
       Shows one framing component at a time with progress indicator + Back/Next.
       All reactive state is identical — only what's rendered changes.
       ════════════════════════════════════════════════════════════════════════ -->
  <div v-if="stepByStep" class="sbf">

    <!-- Progress dots with numbers -->
    <div class="sbf__progress">
      <div
        v-for="(step, i) in steps"
        :key="step.key"
        class="sbf__dot-wrap"
        :class="canGoToStep(i) ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'"
        @click="goToStep(i)"
      >
        <div
          class="sbf__dot"
          :class="{
            'sbf__dot--done':   i < currentStepIndex,
            'sbf__dot--active': i === currentStepIndex,
          }"
        >
          <!-- done: checkmark; active/upcoming: step number -->
          <svg v-if="i < currentStepIndex" viewBox="0 0 12 12" fill="none" class="sbf__check">
            <path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span v-else class="sbf__dot-num">{{ i + 1 }}</span>
        </div>
        <!-- connector line -->
        <div v-if="i < steps.length - 1" class="sbf__connector" :class="{ 'sbf__connector--done': i < currentStepIndex }"></div>
      </div>
    </div>

    <!-- Step header -->
    <div class="sbf__header">
      <h4 class="sbf__title">{{ currentStep.label }}</h4>
      <p v-if="currentStepSubtitle" class="sbf__subtitle">{{ currentStepSubtitle }}</p>
    </div>

    <!-- ── Step content ─────────────────────────────────────────────── -->
    <div class="sbf__content">

      <!-- FRAME -->
      <div v-if="currentStep.key === 'frame'" class="sbf__fields">
        <div class="sbf__field-cost" v-if="frameResult.applicable">
          <span v-if="frameResult.outOfRange || frameResult.rateNotDefined" class="sbf__cost-pill sbf__cost-pill--tbc">To be confirmed</span>
          <span v-else class="sbf__cost-pill">{{ INR(frameResult.cost || 0) }}</span>
        </div>
        <!-- "Frame type" field hidden here — Classic vs Floater is picked automatically (see
             frameType's default) and the step title above now names it instead. frameType itself
             is untouched: still set, still read everywhere else exactly as before. -->
        <div class="mb-4">
          <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Material</label>
          <CustomSelect v-model="frameMaterial" :options="frameMaterialOptions" />
        </div>
        <div class="grid grid-cols-2 gap-4 mb-4 items-end">
          <div>
            <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Front width ({{ unitLabelSuffix }})</label>
            <CustomSelect v-model="frameFront" :options="frameFrontOptions" />
          </div>
          <div>
            <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Depth ({{ unitLabelSuffix }})</label>
            <CustomSelect v-model="frameDepth" :options="frameDepthOptions" />
          </div>
        </div>
        <div v-if="frameType === 'Floater Frame'" class="mb-4">
          <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Floater space ({{ unitLabelSuffix }})</label>
          <CustomSelect v-model="floaterSpace" :options="floaterSpaceOptions" />
        </div>
        <div v-if="frameMaterial === 'Wooden'" class="mb-4">
          <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Finish</label>
          <CustomSelect v-model="frameFinish" :options="frameFinishOptions" />
        </div>
        <div v-if="frameMaterial === 'Wooden' && (frameFinish === 'Painted Polished' || frameFinish === 'Double shade')" class="mb-4">
          <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-2">Color</label>
          <div class="flex items-center gap-3.5 p-1.5 flex-wrap">
            <button
              v-for="c in frameColorOptions"
              :key="c.key"
              type="button"
              @click="frameColor = c.key"
              :title="c.label"
              class="w-8 h-8 rounded-[6px] cursor-pointer bg-cover bg-center transition-all shadow-[inset_0_0_0_1px_rgba(0,0,0,0.15)] focus:outline-none shrink-0"
              :style="{ backgroundImage: `url(${c.img})` }"
              :class="frameColor === c.key ? 'ring-2 ring-offset-2 ring-black scale-105' : 'ring-1 ring-transparent hover:ring-gray-300 hover:scale-105'"
            />
          </div>
          <p class="text-xs text-gray-500 font-medium mt-1">
            {{ frameColorOptions.find(c => c.key === frameColor)?.label }}
          </p>
        </div>
      </div>

      <!-- STRETCHER -->
      <div v-else-if="currentStep.key === 'stretcher'" class="sbf__fields">
        <div class="sbf__field-cost" v-if="stretcherResult.applicable">
          <span v-if="stretcherResult.outOfRange" class="sbf__cost-pill sbf__cost-pill--tbc">To be confirmed</span>
          <span v-else class="sbf__cost-pill">{{ INR(stretcherResult.cost || 0) }}</span>
        </div>
        <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Stretcher bar size ({{ unitLabelSuffix }})</label>
        <CustomSelect v-model="stretcherBar" :disabled="!stretcherResult.applicable" :options="stretcherBarOptions" />
      </div>

      <!-- MOUNT / SIDE DEPTH — combined into one step: both hold the glass off the artwork's
           surface, and a paperwork needs at least one of them, so they're asked together rather
           than as two separate stops the buyer could sail past without picking either. Each gets
           its own card (explainer animation, short description, then its checkbox + options). -->
      <div v-else-if="currentStep.key === 'mountorsidedepth'" class="sbf__fields">
        <p class="text-[13px] text-gray-600 leading-relaxed mb-4">
          Paper must never touch the glass: it can stick, mark or warp over time. A mount or side depth
          keeps a small air gap between the artwork and the glass. Choose at least one.
        </p>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
          <!-- Mount card -->
          <div class="sbf__opt-card" :class="{ 'sbf__opt-card--on': wantMount, 'sbf__opt-card--expanded': wantMount && mountExpandSettled }">
            <div class="px-3 pt-3">
              <div class="flex items-center justify-between gap-2">
                <h5 class="text-sm font-bold text-gray-900">Mount</h5>
                <label class="sbf__toggle-row--compact">
                  <input type="checkbox" v-model="wantMount" class="w-4 h-4 rounded-none border border-gray-300 accent-black cursor-pointer" />
                </label>
              </div>
              <p class="text-xs text-gray-500 mt-0.5 leading-snug min-h-[33px]">Keeps glass off paper.</p>
            </div>
            <div class="px-3 mt-3"><FramingExplainerAnim kind="mount" /></div>
            <div class="px-3 pb-3">
              <div
                class="sbf__expand"
                :class="{ 'sbf__expand--open': wantMount, 'sbf__expand--settled': wantMount && mountExpandSettled }"
                @transitionend="mountExpandSettled = true"
              >
                <div class="sbf__expand-inner flex flex-col gap-3 pt-3">
                  <div class="flex items-center justify-between">
                    <span class="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Cost</span>
                    <span class="text-sm font-medium text-gray-700" v-if="!mountResult.outOfRange">{{ INR(mountResult.cost || 0) }}</span>
                    <span v-else class="text-xs text-gray-500 italic">To be confirmed</span>
                  </div>
                  <div>
                    <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Mount type</label>
                    <CustomSelect v-model="mountType" :options="mountTypeOptions" scroll-into-view-on-open inline-dropdown />
                  </div>
                  <div>
                    <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Mount width ({{ unitLabelSuffix }})</label>
                    <CustomSelect v-model="mountWidth" :options="mountWidthOptions" scroll-into-view-on-open inline-dropdown />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Side depth card -->
          <div class="sbf__opt-card" :class="{ 'sbf__opt-card--on': wantSideDepth, 'sbf__opt-card--expanded': wantSideDepth && sideDepthExpandSettled }">
            <div class="px-3 pt-3">
              <div class="flex items-center justify-between gap-2">
                <h5 class="text-sm font-bold text-gray-900">Side Depth</h5>
                <label class="sbf__toggle-row--compact">
                  <input type="checkbox" v-model="wantSideDepth" class="w-4 h-4 rounded-none border border-gray-300 accent-black cursor-pointer" />
                </label>
              </div>
              <p class="text-xs text-gray-500 mt-0.5 leading-snug min-h-[33px]">A raised lip built into the frame, all the way around.</p>
            </div>
            <div class="px-3 mt-3"><FramingExplainerAnim kind="sidedepth" /></div>
            <div class="px-3 pb-3">
              <div
                class="sbf__expand"
                :class="{ 'sbf__expand--open': wantSideDepth, 'sbf__expand--settled': wantSideDepth && sideDepthExpandSettled }"
                @transitionend="sideDepthExpandSettled = true"
              >
                <div class="sbf__expand-inner flex flex-col gap-3 pt-3">
                  <div class="flex items-center justify-between">
                    <span class="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Cost</span>
                    <span class="text-sm font-medium text-gray-700" v-if="!sideDepthResult.outOfRange">{{ INR(sideDepthResult.cost || 0) }}</span>
                    <span v-else class="text-xs text-gray-500 italic">To be confirmed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p v-if="artworkType === 'Paper' && !wantMount && !wantSideDepth" class="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-3 leading-relaxed">
          ⚠ Select a mount, side depth, or both to continue — for paper works, at least one is required.
        </p>
      </div>

      <!-- GLAZING (Glass + Board + Sheet, combined into one step) -->
      <div v-else-if="currentStep.key === 'glazing'" class="sbf__fields">
        <div class="mb-4">
          <div class="sbf__field-cost" v-if="glassResult.applicable">
            <span v-if="glassResult.outOfRange" class="sbf__cost-pill sbf__cost-pill--tbc">To be confirmed</span>
            <span v-else class="sbf__cost-pill">{{ INR(glassResult.cost || 0) }}</span>
          </div>
          <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Glass type</label>
          <CustomSelect v-model="glassType" :disabled="!glassResult.applicable" :options="glassTypeOptions" />
        </div>

        <div class="mb-4">
          <div class="sbf__field-cost" v-if="boardResult.applicable">
            <span v-if="boardResult.outOfRange" class="sbf__cost-pill sbf__cost-pill--tbc">To be confirmed</span>
            <span v-else class="sbf__cost-pill">{{ INR(boardResult.cost || 0) }}</span>
          </div>
          <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Board type</label>
          <CustomSelect v-model="boardType" :disabled="!boardResult.applicable" :options="boardTypeOptions" />
        </div>

        <div>
          <div class="sbf__field-cost" v-if="sheetResult.applicable">
            <span v-if="sheetResult.outOfRange" class="sbf__cost-pill sbf__cost-pill--tbc">To be confirmed</span>
            <span v-else class="sbf__cost-pill">{{ INR(sheetResult.cost || 0) }}</span>
          </div>
          <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Sheet (PP) rate (₹ / sq ft)</label>
          <input
            type="number"
            v-model.number="sheetRate"
            readonly
            :disabled="!sheetResult.applicable"
            min="0" step="5"
            class="w-full bg-gray-50 border border-gray-200 text-[13px] text-gray-500 rounded-[6px] px-3 py-2 outline-none cursor-default"
          />
        </div>
      </div>

      <!-- SUMMARY — read-only recap of every selection; go Back (or tap a step dot) to change -->
      <div v-else-if="currentStep.key === 'summary'" class="sbf__fields">
        <div class="mb-3 text-xs text-gray-500">Artwork size: {{ formattedSizeLabel }}</div>
        <div v-for="g in summaryGroups" :key="g.title" class="mb-3 pb-3 border-b border-gray-100 last:border-b-0">
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">{{ g.title }}</span>
            <span v-if="g.cost === 'tbc'" class="sbf__cost-pill sbf__cost-pill--tbc">To be confirmed</span>
            <span v-else-if="g.cost !== null" class="sbf__cost-pill">{{ INR(g.cost) }}</span>
          </div>
          <div v-for="row in g.rows" :key="row.label" class="flex items-center justify-between text-[13px] py-0.5">
            <span class="text-gray-500">{{ row.label }}</span>
            <span class="text-gray-800 font-medium text-right">{{ row.value }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Out-of-range warning — full width, with room to actually explain, instead of squeezing a
         sentence into the narrow Total column below (which just wrapped badly). -->
    <p v-if="isOutOfRange" class="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-2 leading-relaxed">
      ⚠ {{ isStretchMode ? 'Stretching' : 'Framing' }} at this size is confirmed personally by our team, so it is
      <strong>not included in the amount payable now</strong>. Your selections are saved with the order and we
      will share the cost with you within 48 hours.
    </p>

    <!-- ── Navigation ────────────────────────────────────────────────── -->
    <div class="flex items-center justify-between gap-4 pt-4 mt-auto border-t border-on-primary-border">
      <Button
        variant="secondary"
        size="sm"
        :disabled="isFirstStep"
        :class="{ invisible: isFirstStep }"
        @click.stop.prevent="goPrev"
        class="!px-4 !py-2.5"
      >
        <Icon name="lucide:chevron-left" class="size-4" />
        Back
      </Button>

      <!-- Running total -->
      <div class="flex flex-col items-center gap-0.5">
        <span class="text-[10px] font-semibold tracking-widest text-on-primary-body uppercase">Total</span>
        <span v-if="isOutOfRange" class="text-base font-bold text-on-primary-heading tracking-tight">-</span>
        <span v-else class="text-base font-bold text-on-primary-heading tracking-tight">{{ INR(framingTotal) }}</span>
      </div>

      <Button
        v-if="!isLastStep"
        variant="primary"
        size="sm"
        :disabled="!isCurrentStepValid"
        @click.stop.prevent="goNext"
        class="!px-5 !py-2.5"
      >
        Next
        <Icon name="lucide:chevron-right" class="size-4" />
      </Button>

      <Button
        v-else
        variant="primary"
        size="sm"
        :disabled="!isCurrentStepValid"
        @click.prevent="emit('done')"
        class="!px-5 !py-2.5"
      >
        Done
        <Icon name="lucide:check" class="size-4 ml-0.5" />
      </Button>
    </div>

  </div><!-- /sbf -->


  <!-- ════════════════════════════════════════════════════════════════════════
       ORIGINAL ACCORDION MODE  (default — stepByStep not set or false)
       Completely unchanged.
       ════════════════════════════════════════════════════════════════════════ -->
  <div v-else class="bg-[#f8f9fa] border border-gray-100 rounded-[8px] p-4 flex flex-col gap-3 mt-2">
    
    <!-- Header. Doubles as the panel's own toggle, matching how each component below opens. -->
    <div class="cursor-pointer select-none" @click="isPanelOpen = !isPanelOpen">
      <div class="flex items-center justify-between gap-3 border-b pb-1">
        <h5 class="font-bold text-xs uppercase tracking-widest">{{ isStretchMode ? 'Stretching options' : 'Framing options' }}</h5>
        <Icon name="lucide:chevron-down" class="w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200" :class="{ 'rotate-180': isPanelOpen }" />
      </div>
      <p class="text-[13px] text-gray-500 mt-2">
        {{ isStretchMode ? 'Select your stretcher bar below.' : 'Select your frame specifications below.' }}
      </p>
      <!-- Structural, not just a validation message: Mount and Side Depth are independently
           optional, but for Paper at least one of the two must exist — nothing else in a Paper
           build holds the glass off the artwork's surface. Stated here up front, alongside where
           the rest of the framing rules already read, rather than only surfacing as an error after
           the buyer tries to pay with neither selected (see needsMountOrSideDepth). -->
      <p v-if="artworkType === 'Paper' && !isStretchMode" class="text-[12px] text-gray-500 mt-1.5">
        *For paper works, either a mount or side depth or both are required to create space between the artwork and the glass, ensuring the artwork never sits directly against it.
      </p>
    </div>

    <!-- The specifications themselves. v-show rather than a max-height transition: this wrapper is
         far taller than the 800px the component rows animate within, and a clipped panel would hide
         controls with no way to reach them. -->
    <div v-show="isPanelOpen" class="flex flex-col gap-3">

    <!-- FRAME -->
    <div v-show="!isStretchMode" class="bg-white border border-gray-200 rounded-[8px] transition-all duration-200" :class="[ !frameResult.applicable ? 'opacity-50 pointer-events-none' : '', openComps.frame ? 'border-gray-300 shadow-sm overflow-visible z-10' : 'overflow-hidden' ]">
      <div class="flex items-center justify-between p-3.5 cursor-pointer select-none" @click="toggleComp('frame')">
        <div class="flex items-center gap-1.5">
          <span class="text-[13px] font-semibold text-gray-900">Frame</span>
          <UTooltip text="Outer decorative border" :content="{ side: 'bottom', align: 'start' }">
            <Icon name="material-symbols:info-outline" class="w-3.5 h-3.5 text-on-primary-body" @click.stop />
          </UTooltip>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-[14px] font-medium text-gray-700" v-if="frameResult.outOfRange || frameResult.rateNotDefined">—</span>
          <span class="text-[14px] font-medium text-gray-700" v-else-if="frameResult.applicable">{{ INR(frameResult.cost || 0) }}</span>
          <span class="text-[14px] font-medium text-gray-700" v-else>N/A</span>
          <Icon name="lucide:chevron-down" class="w-4 h-4 text-gray-400 shrink-0 ml-1 transition-transform duration-200" :class="{ 'rotate-180': openComps.frame }" />
        </div>
      </div>
      <div class="transition-all duration-300" :class="openComps.frame ? 'overflow-visible' : 'overflow-hidden'" :style="{ maxHeight: openComps.frame ? '800px' : '0' }">
        <div class="p-4 border-t border-gray-100 bg-[#f8f9fa] rounded-b-[8px]">
          <div class="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Frame type</label>
              <CustomSelect v-model="frameType" :disabled="!frameResult.applicable" :options="frameTypeOptions" />
            </div>
            <div>
              <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Material</label>
              <CustomSelect v-model="frameMaterial" :disabled="!frameResult.applicable" :options="frameMaterialOptions" />
            </div>
          </div>
          <div class="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Front width ({{ inUnitLabelSuffix }})</label>
              <CustomSelect v-model="frameFront" :disabled="!frameResult.applicable" :options="frameFrontOptions" />
            </div>
            <div>
              <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Depth ({{ inUnitLabelSuffix }})</label>
              <CustomSelect v-model="frameDepth" :disabled="!frameResult.applicable" :options="frameDepthOptions" />
            </div>
          </div>
          <div class="grid grid-cols-1 gap-4 mb-4" v-if="frameType === 'Floater Frame'">
            <div>
              <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Floater space ({{ inUnitLabelSuffix }})</label>
              <CustomSelect v-model="floaterSpace" :disabled="!frameResult.applicable" :options="floaterSpaceOptions" />
            </div>
          </div>
          <div class="grid grid-cols-1 gap-4" v-if="frameMaterial === 'Wooden'">
            <div>
              <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Finish</label>
              <CustomSelect v-model="frameFinish" :disabled="!frameResult.applicable" :options="frameFinishOptions" />
            </div>
          </div>
          <div class="mt-4" v-if="frameMaterial === 'Wooden' && (frameFinish === 'Painted Polished' || frameFinish === 'Double shade')">
             <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-2">Color</label>
             <div class="flex gap-4" :class="{ 'opacity-50 pointer-events-none': !frameResult.applicable }">
               <div v-for="c in frameColorOptions" :key="c.key" @click="frameColor = c.key" :title="c.label" class="w-[28px] h-[28px] rounded-[6px] cursor-pointer bg-cover bg-center transition-all shadow-[inset_0_0_0_1px_rgba(0,0,0,0.12)]" :style="{ backgroundImage: `url(${c.img})` }" :class="frameColor === c.key ? 'ring-2 ring-offset-2 ring-black scale-110' : 'ring-1 ring-transparent hover:ring-gray-300'"></div>
             </div>
          </div>
        </div>
      </div>
    </div>

    <!-- STRETCHER -->
    <div class="bg-white border border-gray-200 rounded-[8px] transition-all duration-200" :class="[ !stretcherResult.applicable ? 'opacity-50 pointer-events-none' : '', openComps.stretcher ? 'border-gray-300 shadow-sm overflow-visible z-10' : 'overflow-hidden' ]">
      <div class="flex items-center justify-between p-3.5 cursor-pointer select-none" @click="toggleComp('stretcher')">
        <div class="flex items-center gap-1.5">
          <span class="text-[13px] font-semibold text-gray-900">Stretcher</span>
          <UTooltip text="Wooden support the canvas is stretched on" :content="{ side: 'bottom', align: 'start' }">
            <Icon name="material-symbols:info-outline" class="w-3.5 h-3.5 text-on-primary-body" @click.stop />
          </UTooltip>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-[14px] font-medium text-gray-700" v-if="stretcherResult.outOfRange">—</span>
          <span class="text-[14px] font-medium text-gray-700" v-else-if="stretcherResult.applicable">{{ INR(stretcherResult.cost || 0) }}</span>
          <span class="text-[14px] font-medium text-gray-700" v-else>N/A</span>
          <Icon name="lucide:chevron-down" class="w-4 h-4 text-gray-400 shrink-0 ml-1 transition-transform duration-200" :class="{ 'rotate-180': openComps.stretcher }" />
        </div>
      </div>
      <div class="transition-all duration-300" :class="openComps.stretcher ? 'overflow-visible' : 'overflow-hidden'" :style="{ maxHeight: openComps.stretcher ? '800px' : '0' }">
        <div class="p-4 border-t border-gray-100 bg-[#f8f9fa] rounded-b-[8px]">
          <div class="grid grid-cols-1 gap-4">
            <div>
              <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Stretcher bar size ({{ inUnitLabelSuffix }})</label>
              <CustomSelect v-model="stretcherBar" :disabled="!stretcherResult.applicable" :options="stretcherBarOptions" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MOUNT -->
    <div v-show="!isStretchMode && artworkType !== 'Canvas'" class="bg-white border border-gray-200 rounded-[8px] transition-all duration-200" :class="[ openComps.mount ? 'border-gray-300 shadow-sm overflow-visible z-10' : 'overflow-hidden' ]">
      <div class="flex items-center justify-between p-3.5 cursor-pointer select-none" @click="toggleComp('mount')">
        <div class="flex items-center gap-1.5">
          <span class="text-[13px] font-semibold text-gray-900">Mount</span>
          <UTooltip text="Paper border around the artwork" :content="{ side: 'bottom', align: 'start' }">
            <Icon name="material-symbols:info-outline" class="w-3.5 h-3.5 text-on-primary-body" @click.stop />
          </UTooltip>
        </div>
        <div class="flex items-center gap-3">
          <input type="checkbox" v-model="wantMount" @click.stop class="w-4 h-4 rounded-none border border-gray-300 accent-black cursor-pointer" />
          <span class="text-[14px] font-medium text-gray-700" v-if="mountResult.outOfRange">—</span>
          <span class="text-[14px] font-medium text-gray-700" v-else-if="mountResult.applicable">{{ INR(mountResult.cost || 0) }}</span>
          <Icon name="lucide:chevron-down" class="w-4 h-4 text-gray-400 shrink-0 ml-1 transition-transform duration-200" :class="{ 'rotate-180': openComps.mount }" />
        </div>
      </div>
      <div class="transition-all duration-300" :class="openComps.mount ? 'overflow-visible' : 'overflow-hidden'" :style="{ maxHeight: openComps.mount ? '800px' : '0' }">
        <div class="p-4 border-t border-gray-100 bg-[#f8f9fa] rounded-b-[8px]">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Mount type</label>
              <CustomSelect v-model="mountType" :disabled="!mountResult.applicable || !wantMount" :options="mountTypeOptions" />
            </div>
            <div>
              <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Mount width ({{ inUnitLabelSuffix }})</label>
              <CustomSelect v-model="mountWidth" :disabled="!mountResult.applicable || !wantMount" :options="mountWidthOptions" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- SIDE DEPTH -->
    <div v-show="!isStretchMode && artworkType !== 'Canvas'" class="bg-white border border-gray-200 rounded-[8px] transition-all duration-200">
      <div class="flex items-center justify-between p-3.5 select-none">
        <div class="flex items-center gap-1.5">
          <span class="text-[13px] font-semibold text-gray-900">Side Depth</span>
          <UTooltip text="Spacer strip keeping glass off the art" :content="{ side: 'bottom', align: 'start' }">
            <Icon name="material-symbols:info-outline" class="w-3.5 h-3.5 text-on-primary-body" @click.stop />
          </UTooltip>
        </div>
        <div class="flex items-center gap-3">
          <input type="checkbox" v-model="wantSideDepth" class="w-4 h-4 rounded-none border border-gray-300 accent-black cursor-pointer" />
          <span class="text-[14px] font-medium text-gray-700" v-if="sideDepthResult.outOfRange">—</span>
          <span class="text-[14px] font-medium text-gray-700" v-else-if="sideDepthResult.applicable">{{ INR(sideDepthResult.cost || 0) }}</span>
          <div class="w-4 h-4 shrink-0 ml-1"></div>
        </div>
      </div>
    </div>

    <!-- GLASS -->
    <div v-show="!isStretchMode && artworkType !== 'Canvas'" class="bg-white border border-gray-200 rounded-[8px] transition-all duration-200" :class="[ !glassResult.applicable ? 'opacity-50 pointer-events-none' : '', openComps.glass ? 'border-gray-300 shadow-sm overflow-visible z-10' : 'overflow-hidden' ]">
      <div class="flex items-center justify-between p-3.5 cursor-pointer select-none" @click="toggleComp('glass')">
        <div class="flex items-center gap-1.5">
          <span class="text-[13px] font-semibold text-gray-900">Glass</span>
          <UTooltip text="Protective cover over the front" :content="{ side: 'bottom', align: 'start' }">
            <Icon name="material-symbols:info-outline" class="w-3.5 h-3.5 text-on-primary-body" @click.stop />
          </UTooltip>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-[14px] font-medium text-gray-700" v-if="glassResult.outOfRange">—</span>
          <span class="text-[14px] font-medium text-gray-700" v-else-if="glassResult.applicable">{{ INR(glassResult.cost || 0) }}</span>
          <span class="text-[14px] font-medium text-gray-700" v-else>N/A</span>
          <Icon name="lucide:chevron-down" class="w-4 h-4 text-gray-400 shrink-0 ml-1 transition-transform duration-200" :class="{ 'rotate-180': openComps.glass }" />
        </div>
      </div>
      <div class="transition-all duration-300" :class="openComps.glass ? 'overflow-visible' : 'overflow-hidden'" :style="{ maxHeight: openComps.glass ? '800px' : '0' }">
        <div class="p-4 border-t border-gray-100 bg-[#f8f9fa] rounded-b-[8px]">
          <div class="grid grid-cols-1 gap-4">
            <div>
              <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Glass type</label>
              <CustomSelect v-model="glassType" :disabled="!glassResult.applicable" :options="glassTypeOptions" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- BOARD -->
    <div v-show="!isStretchMode && artworkType !== 'Canvas'" class="bg-white border border-gray-200 rounded-[8px] transition-all duration-200" :class="[ !boardResult.applicable ? 'opacity-50 pointer-events-none' : '', openComps.board ? 'border-gray-300 shadow-sm overflow-visible z-10' : 'overflow-hidden' ]">
      <div class="flex items-center justify-between p-3.5 cursor-pointer select-none" @click="toggleComp('board')">
        <div class="flex items-center gap-1.5">
          <span class="text-[13px] font-semibold text-gray-900">Board</span>
          <UTooltip text="Solid backing behind the artwork" :content="{ side: 'bottom', align: 'start' }">
            <Icon name="material-symbols:info-outline" class="w-3.5 h-3.5 text-on-primary-body" @click.stop />
          </UTooltip>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-[14px] font-medium text-gray-700" v-if="boardResult.outOfRange">—</span>
          <span class="text-[14px] font-medium text-gray-700" v-else-if="boardResult.applicable">{{ INR(boardResult.cost || 0) }}</span>
          <span class="text-[14px] font-medium text-gray-700" v-else>N/A</span>
          <Icon name="lucide:chevron-down" class="w-4 h-4 text-gray-400 shrink-0 ml-1 transition-transform duration-200" :class="{ 'rotate-180': openComps.board }" />
        </div>
      </div>
      <div class="transition-all duration-300" :class="openComps.board ? 'overflow-visible' : 'overflow-hidden'" :style="{ maxHeight: openComps.board ? '800px' : '0' }">
        <div class="p-4 border-t border-gray-100 bg-[#f8f9fa] rounded-b-[8px]">
          <div class="grid grid-cols-1 gap-4">
            <div>
              <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Board type</label>
              <CustomSelect v-model="boardType" :disabled="!boardResult.applicable" :options="boardTypeOptions" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- SHEET -->
    <div v-show="!isStretchMode && artworkType !== 'Canvas'" class="bg-white border border-gray-200 rounded-[8px] transition-all duration-200" :class="[ !sheetResult.applicable ? 'opacity-50 pointer-events-none' : '', openComps.sheet ? 'border-gray-300 shadow-sm overflow-visible z-10' : 'overflow-hidden' ]">
      <div class="flex items-center justify-between p-3.5 cursor-pointer select-none" @click="toggleComp('sheet')">
        <div class="flex items-center gap-1.5">
          <span class="text-[13px] font-semibold text-gray-900">Sheet (PP)</span>
          <UTooltip text="Protective plastic sheet" :content="{ side: 'bottom', align: 'start' }">
            <Icon name="material-symbols:info-outline" class="w-3.5 h-3.5 text-on-primary-body" @click.stop />
          </UTooltip>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-[14px] font-medium text-gray-700" v-if="sheetResult.outOfRange">—</span>
          <span class="text-[14px] font-medium text-gray-700" v-else-if="sheetResult.applicable">{{ INR(sheetResult.cost || 0) }}</span>
          <span class="text-[14px] font-medium text-gray-700" v-else>N/A</span>
          <Icon name="lucide:chevron-down" class="w-4 h-4 text-gray-400 shrink-0 ml-1 transition-transform duration-200" :class="{ 'rotate-180': openComps.sheet }" />
        </div>
      </div>
      <div class="transition-all duration-300" :class="openComps.sheet ? 'overflow-visible' : 'overflow-hidden'" :style="{ maxHeight: openComps.sheet ? '800px' : '0' }">
        <div class="p-4 border-t border-gray-100 bg-[#f8f9fa]">
          <div class="grid grid-cols-1 gap-4">
            <div>
              <label class="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Rate (₹ / sq ft)</label>
              <input type="number" v-model.number="sheetRate" readonly :disabled="!sheetResult.applicable" min="0" step="5" class="w-full bg-gray-50 border border-gray-200 text-[13px] text-gray-500 rounded-[6px] px-3 py-2 outline-none cursor-default">
            </div>
          </div>
        </div>
      </div>
    </div>

    </div><!-- /specifications -->

    <!-- Total. Always visible, open or closed. A size we cannot fully price shows no figure at all
         — see isOutOfRange: the partial sum that would otherwise appear here is not charged. -->
    <div class="bg-white border border-gray-200 rounded-[8px] flex flex-col gap-2 p-4">
      <div class="flex items-center justify-between gap-4">
        <div class="text-[13px] font-bold text-[#1a1a2e] uppercase tracking-widest">{{ totalLabel || (isStretchMode ? 'Stretching Total' : 'Framing Total') }}</div>
        <div v-if="isOutOfRange" class="text-[14px] font-bold text-[#1a1a2e] shrink-0">To be confirmed</div>
        <div v-else class="text-[16px] font-bold text-[#1a1a2e]">{{ INR(framingTotal) }}</div>
      </div>
      <p v-if="isOutOfRange" class="text-[12px] text-gray-500 leading-relaxed">
        {{ isStretchMode ? 'Stretching' : 'Framing' }} at this size is confirmed personally by our team, so it is
        <strong>not included in the amount payable now</strong>. Your selections are saved with the order and we
        will share the cost with you within 48 hours.
      </p>
    </div>

  </div><!-- /accordion mode -->

</template>

<style scoped>
/* ── Step-by-step framing (sbf) ─────────────────────────────────────────────── */
.sbf {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 1rem;
}

/* Progress dots row */
.sbf__progress {
  display: flex;
  align-items: center;
  gap: 0;
  width: 100%;
}
.sbf__dot-wrap {
  display: flex;
  align-items: center;
  flex: 1;
}
.sbf__dot-wrap:last-child {
  flex: 0;
}
.sbf__dot {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s ease;
  cursor: pointer;
}
.sbf__dot-wrap:hover .sbf__dot {
  border-color: var(--color-on-primary-heading, #111);
  transform: scale(1.1);
}
.sbf__dot--active {
  border-color: var(--color-on-primary-heading, #111);
  background: var(--color-on-primary-heading, #111);
}
.sbf__dot--done {
  border-color: var(--color-on-primary-heading, #111);
  background: var(--color-on-primary-heading, #111);
}
.sbf__dot-num {
  font-size: 0.75rem;
  font-weight: 700;
  color: #9ca3af;
  line-height: 1;
}
.sbf__dot--active .sbf__dot-num {
  color: #fff;
}
.sbf__check {
  width: 0.75rem;
  height: 0.75rem;
  color: #fff;
}
.sbf__connector {
  flex: 1;
  height: 2px;
  background: #e5e7eb;
  transition: background 0.2s;
}
.sbf__connector--done {
  background: var(--color-on-primary-heading, #111);
}

/* Mount / side depth option cards */
.sbf__opt-card {
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.sbf__opt-card--on {
  border-color: var(--color-on-primary-heading, #111);
  box-shadow: inset 0 0 0 1px var(--color-on-primary-heading, #111);
}
/* overflow:hidden above exists to keep children inside the rounded corners. Once the card's own
   options area has finished sliding open, lift that clipping so an open <CustomSelect> dropdown
   inside it isn't cut off by the card's own edge. */
.sbf__opt-card--expanded {
  overflow: visible;
}

/* Step header */
.sbf__header {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0;
}
.sbf__title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-on-primary-heading, #111);
  margin: 0;
  letter-spacing: -0.01em;
}
.sbf__subtitle {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0.25rem 0 0;
  line-height: 1.4;
}

/* Step content area — fills remaining space and scrolls; never drives dialog height */
.sbf__content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 0 0.75rem 0;
}
@media (max-width: 1023px) {
  .sbf__content {
    flex: initial;
    min-height: auto;
    overflow-y: visible;
  }
}
.sbf__fields {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.sbf__field-cost {
  margin-bottom: 1rem;
}
.sbf__cost-pill {
  display: inline-flex;
  align-items: center;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--color-on-primary-heading, #111);
  letter-spacing: -0.01em;
}
.sbf__cost-pill--tbc {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-on-primary-body, #6b7280);
  font-style: italic;
}

/* Toggle row for optional fields (Mount / Side Depth) */
.sbf__toggle-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.875rem 1rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  cursor: pointer;
  user-select: none;
}

/* Compact variant: just the checkbox + "Add", sitting beside the card's heading instead of its
   own boxed row. */
.sbf__toggle-row--compact {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  cursor: pointer;
  user-select: none;
  flex-shrink: 0;
}

/* Mount/Side Depth cost + options area: animates open/closed instead of popping in when the
   checkbox is toggled. grid-template-rows 0fr->1fr is the standard CSS-only trick for animating
   to an "auto" height; the inner wrapper's overflow:hidden is what actually clips it mid-transition. */
.sbf__expand {
  display: grid;
  grid-template-rows: 0fr;
  overflow: hidden;
  transition: grid-template-rows 220ms ease;
}
.sbf__expand--open {
  grid-template-rows: 1fr;
}
.sbf__expand-inner {
  overflow: hidden;
  min-height: 0;
}
/* Once the slide-open transition has actually finished (not just started), let this area overflow
   visibly again — otherwise a <CustomSelect> dropdown inside it (itself position:absolute) would
   stay clipped by the very clipping that makes the open/close animation work. */
.sbf__expand--settled {
  overflow: visible;
}
.sbf__expand--settled > .sbf__expand-inner {
  overflow: visible;
}

</style>
